import moment, { Moment } from 'moment-timezone';
import StatusHelper from '@wix/wixrest-utils/dist/openrest-helpers/Status';
import { DispatchInfo, Restaurant } from '../types/Restaurant';
import { DisplayableMenu } from '../types/Menu';
import { DispatchType, VirtualDispatchType } from '../types/Dispatch';
import {
  getIdealDeliveryArea,
  getMinimalOrderPriceForAllAreas,
} from './addressLogic';
import { Address } from '../types/Address';

function getDisplayablePrice(
  price: number = 0,
  locale: string,
  currency: string,
): string {
  const formattedLocale = locale.replace('_', '-');
  const numberFormat = new Intl.NumberFormat(formattedLocale, {
    style: 'currency',
    currency,
  });
  return numberFormat.format(price / 100);
}

interface GetDefaultDispatchTypeArgs {
  restaurant: Restaurant;
  displayableMenu: DisplayableMenu;
  dispatchTime: number;
}

export function getDefaultDispatchType({
  restaurant,
  dispatchTime,
  displayableMenu,
}: GetDefaultDispatchTypeArgs): DispatchType {
  const dispatchTypes = restaurant.deliveryInfos
    .filter((di) =>
      isDispatchInfoActive(di, moment(dispatchTime).tz(restaurant.timezone)),
    )
    .map((di) => di.type);
  const uniqueDispatchTypes = Array.from(new Set(dispatchTypes));
  const foundDeliverableItems = hasAnyDeliverableItems(displayableMenu);
  const isDeliverySupported =
    foundDeliverableItems && uniqueDispatchTypes.includes('delivery');
  const customDefaultDispatchType =
    restaurant.defaultDispatchType || 'delivery';

  if (uniqueDispatchTypes.length === 0) {
    throw new Error('getDefaultDispatchType: No dispatch type found');
  } else if (uniqueDispatchTypes.length === 1) {
    return uniqueDispatchTypes[0];
  } else if (customDefaultDispatchType === 'delivery') {
    return isDeliverySupported ? customDefaultDispatchType : 'takeout';
  } else {
    return customDefaultDispatchType;
  }
}

export function hasAnyDeliverableItems(
  displayableMenu: DisplayableMenu,
): boolean {
  return displayableMenu
    .map((_menu) =>
      _menu.sections.map((section) => section.items.length > 0).some(Boolean),
    )
    .some(Boolean);
}

function isDispatchInfoActive(
  dispatchInfo: DispatchInfo,
  dispatchTime: Moment,
) {
  const { status, reason } = StatusHelper.getStatus(
    dispatchInfo.availability,
    dispatchTime,
  );
  const isClosed = status === 'unavailable' && reason === 'closed';
  return dispatchInfo.inactive !== false && !isClosed;
}

export function isMinimumPriceMet({
  totalOrderPrice,
  restaurant,
  dispatchType,
  selectedAddressOption,
}: {
  totalOrderPrice: number;
  restaurant: Restaurant;
  dispatchType: VirtualDispatchType;
  selectedAddressOption?: Address;
}): boolean {
  const minOrderPrice = getMinOrderPrice({
    totalOrderPrice,
    restaurant,
    dispatchType,
    selectedAddressOption,
  });
  return totalOrderPrice >= minOrderPrice;
}

export function getMinimalPickupOrderPrice(deliveryInfos: DispatchInfo[]) {
  const takeout = deliveryInfos.find((di) => di.type === 'takeout');
  const minOrderPrice = takeout?.minOrderPrice || 0;
  return minOrderPrice;
}

export function getMinOrderPrice({
  totalOrderPrice,
  restaurant,
  dispatchType,
  selectedAddressOption,
}: {
  totalOrderPrice: number;
  restaurant: Restaurant;
  dispatchType: VirtualDispatchType;
  selectedAddressOption?: Address;
}) {
  if (dispatchType === 'dine-in') {
    return 0;
  }
  if (selectedAddressOption && dispatchType === 'delivery') {
    const idealDeliveryArea = getIdealDeliveryArea({
      totalOrderPrice,
      address: selectedAddressOption,
      restaurant,
    });
    return (
      idealDeliveryArea.reason?.minPrice ||
      idealDeliveryArea.dispatchInfo?.minOrderPrice ||
      0
    );
  } else {
    return (
      (dispatchType === 'delivery'
        ? getMinimalOrderPriceForAllAreas(restaurant.deliveryInfos)
        : getMinimalPickupOrderPrice(restaurant.deliveryInfos)) || 0
    );
  }
}

export function getMinOrderPriceDetails({
  totalOrderPrice,
  restaurant,
  dispatchType,
  selectedAddressOption,
}: {
  totalOrderPrice: number;
  restaurant: Restaurant;
  dispatchType: VirtualDispatchType;
  selectedAddressOption?: Address;
}) {
  const minOrderPrice = getMinOrderPrice({
    totalOrderPrice,
    restaurant,
    dispatchType,
    selectedAddressOption,
  });
  const amountLeft = Math.max(minOrderPrice - totalOrderPrice, 0);
  const displayableMinOrderPrice = minOrderPrice
    ? getDisplayablePrice(minOrderPrice, restaurant.locale, restaurant.currency)
    : undefined;
  const displayableAmountLeft = amountLeft
    ? getDisplayablePrice(amountLeft, restaurant.locale, restaurant.currency)
    : undefined;

  return {
    minOrderPrice,
    displayableMinOrderPrice,
    amountLeft,
    displayableAmountLeft,
  };
}
